import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Loader2, Sparkles } from "lucide-react";
import { getAIChatConfig, sendGeminiMessage } from "../services/aiChat";
import { listAll } from "../services/firestore";
import { useApp } from "../contexts/AppContext";

// Persistent chat history per browser session
const STORAGE_KEY = "vinylbaku_ai_chat_history_v1";
const TOOLTIP_SEEN_KEY = "vinylbaku_ai_chat_tooltip_seen_v1";

/* ---------- Product token parsing ----------
   AI wraps recommendations as [[product:slug]] inline anywhere in the reply.
   We split the text on those tokens and render small product cards
   between the text fragments, ordered as the AI placed them.
------------------------------------------------*/
const PRODUCT_RE = /\[\[product:([a-z0-9_-]+)\]\]/gi;

function parseMessageParts(text) {
  const parts = [];
  let lastIdx = 0;
  let m;
  while ((m = PRODUCT_RE.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push({ type: "text", value: text.slice(lastIdx, m.index) });
    }
    parts.push({ type: "product", slug: m[1].toLowerCase() });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    parts.push({ type: "text", value: text.slice(lastIdx) });
  }
  return parts;
}

export default function AIChat() {
  const { formatPrice } = useApp();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [artists, setArtists] = useState([]);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load config + products from Firestore on first mount
  useEffect(() => {
    let mounted = true;
    Promise.all([getAIChatConfig(), listAll("products"), listAll("artists")])
      .then(([c, p, a]) => {
        if (!mounted) return;
        setCfg(c);
        setProducts(p || []);
        setArtists(a || []);
        setLoaded(true);
      })
      .catch(() => { if (mounted) setLoaded(true); });
    return () => { mounted = false; };
  }, []);

  // Show animated tooltip a couple seconds after first load (once per session)
  useEffect(() => {
    if (!loaded || !cfg || cfg.enabled === false) return;
    if (sessionStorage.getItem(TOOLTIP_SEEN_KEY) === "1") return;
    const t1 = setTimeout(() => setShowTooltip(true), 1800);
    const t2 = setTimeout(() => {
      setShowTooltip(false);
      try { sessionStorage.setItem(TOOLTIP_SEEN_KEY, "1"); } catch { /* ignore */ }
    }, 9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loaded, cfg]);

  // Persist conversation
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
    } catch { /* ignore */ }
  }, [messages]);

  // Auto-scroll on new message
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, busy]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 220);
    if (open) setShowTooltip(false);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Build a compact product catalog string for the system prompt
  const catalogPrompt = useMemo(() => {
    if (!products.length) return "";
    const artistById = Object.fromEntries(artists.map((a) => [a.id, a.name]));
    // Sort: newest first if available, then take top 35 to keep tokens small
    const list = [...products]
      .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0))
      .slice(0, 35)
      .map((p) => {
        const artist = artistById[p.artist_id] || "";
        const price = `${Math.round(Number(p.price_azn) || 0)} AZN`;
        const disc = Number(p.discount_pct) > 0 ? ` (-${Number(p.discount_pct)}%)` : "";
        return `- slug:${p.slug} | ${p.title}${artist ? " — " + artist : ""} | ${price}${disc}`;
      })
      .join("\n");
    return `\n\nMÖVCUD MƏHSULLAR (mağaza kataloqu):\n${list}\n\nQAYDA: Müştəri konkret musiqi, sənətçi, janr, hədiyyə və ya məhsul soruşduqda yuxarıdakı siyahıdan uyğun olanları məsləhət gör. HƏR məhsul üçün cavab mətninin içində MÜTLƏQ bu xüsusi formatda işarə qoy: [[product:SLUG]] — burada SLUG yuxarıdakı siyahıdakı "slug:" dəyəridir. İşarələr cavab içində birbaşa görsənəcək (sistem onları məhsul kartlarına çevirir). 1-3 məhsul tövsiyə et — daha çox yox. Əgər müştəri ümumi sual verirsə (qaydalar, çatdırılma, hədiyyə kartı və s.) — məhsul siyahısı GÖSTƏRMƏ.`;
  }, [products, artists]);

  if (!loaded || !cfg || cfg.enabled === false) return null;

  const productBySlug = (slug) => products.find((p) => p.slug === slug);
  const artistById = Object.fromEntries(artists.map((a) => [a.id, a.name]));

  // ─── Resolved UI strings (admin-editable with safe fallbacks) ─────────
  const T = {
    headerTitle:      cfg.header_title      || "Vinyl Baku AI",
    headerSubtitle:   cfg.header_subtitle   || "Köməkçi",
    tooltipText:      cfg.tooltip_text      || "Sizə necə kömək göstərə bilərəm?",
    inputPlaceholder: cfg.input_placeholder || "Sualınızı yazın...",
    typingText:       cfg.typing_text       || "Yazır…",
    quickPromptsLabel:cfg.quick_prompts_label || "Sual verin",
    clearButtonText:  cfg.clear_button_text || "Təmizlə",
    fallbackError:    cfg.fallback_error_text ||
                      "Üzr istəyirəm, hal-hazırda cavab verə bilmirəm. Bir az sonra yenidən cəhd edin.",
    launcherLabel:    (cfg.launcher_label || "V").trim() || "V",
  };
  const suggestedPrompts = Array.isArray(cfg.suggested_prompts) && cfg.suggested_prompts.length
    ? cfg.suggested_prompts
    : [
        "Klassik vinyl tövsiyə et",
        "Hədiyyəlik nə alım?",
        "Çatdırılma necə işləyir?",
      ];
  const hasLogo = !!(cfg.logo_url && String(cfg.logo_url).trim());

  // Small reusable avatar that renders the custom logo (circular) when
  // configured, otherwise falls back to the legacy serif initial.
  // eslint-disable-next-line react/prop-types
  const Avatar = ({ size = 32, className = "" }) => (
    hasLogo ? (
      <img
        src={cfg.logo_url}
        alt={T.headerTitle}
        className={`rounded-full object-cover shrink-0 ${className}`}
        style={{ width: size, height: size }}
        loading="lazy"
      />
    ) : (
      <span
        className={`rounded-full bg-black text-white flex items-center justify-center shrink-0 font-serif leading-none ${className}`}
        style={{ width: size, height: size, fontFamily: "'Playfair Display', serif", fontSize: Math.max(12, Math.floor(size * 0.5)) }}
      >
        {T.launcherLabel.charAt(0).toUpperCase()}
      </span>
    )
  );

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setError("");
    const userMsg = { role: "user", text, ts: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const history = next.slice(-12).map((m) => ({ role: m.role, text: m.text }));
      history.pop(); // remove current user message
      const reply = await sendGeminiMessage({
        apiKey: cfg.api_key,
        model: cfg.model,
        systemInstruction: (cfg.system_prompt || "") + catalogPrompt,
        history,
        userText: text,
      });
      setMessages((m) => [...m, { role: "model", text: reply, ts: Date.now() }]);
    } catch (err) {
      console.error("[AIChat] send error", err);
      setError(err.message || "Xəta baş verdi.");
      setMessages((m) => [
        ...m,
        { role: "model", text: T.fallbackError, ts: Date.now(), error: true },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const showWelcome = messages.length === 0;

  // Render a single model/user message — text + interleaved product cards
  const renderMessageContent = (m) => {
    if (m.role !== "model") return <span>{m.text}</span>;
    const parts = parseMessageParts(m.text);
    return (
      <div className="space-y-2">
        {parts.map((p, i) => {
          if (p.type === "text") {
            const v = p.value.trim();
            if (!v) return null;
            return <div key={i} className="whitespace-pre-wrap break-words">{v}</div>;
          }
          const prod = productBySlug(p.slug);
          if (!prod) return null;
          const discountPct = Math.max(0, Math.min(90, Number(prod.discount_pct) || 0));
          const hasDiscount = discountPct > 0;
          const finalPrice = hasDiscount
            ? Number(prod.price_azn) * (1 - discountPct / 100)
            : Number(prod.price_azn);
          return (
            <Link
              key={i}
              to={`/product/${prod.slug}`}
              onClick={() => setOpen(false)}
              className="flex gap-3 items-stretch p-2 rounded-xl border border-black/10 bg-white hover:border-black hover:shadow-md transition-all group"
              data-testid={`ai-product-${prod.slug}`}
            >
              <div className="w-16 h-16 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
                {prod.cover_url ? (
                  <img
                    src={prod.cover_url}
                    alt={prod.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-black/30 text-xl font-serif">V</div>
                )}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="text-[10px] tracking-[0.16em] uppercase text-black/40 truncate">{artistById[prod.artist_id] || ""}</div>
                <div className="text-[13px] font-medium text-black leading-snug truncate mt-0.5">{prod.title}</div>
                <div className="text-[12px] font-mono mt-1 flex items-center gap-1.5">
                  {hasDiscount ? (
                    <>
                      <span className="line-through text-black/40">{formatPrice(prod.price_azn)}</span>
                      <span className="text-[var(--accent)] font-medium">{formatPrice(finalPrice)}</span>
                    </>
                  ) : (
                    <span className="text-black">{formatPrice(prod.price_azn)}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {/* Floating launcher — circular, bottom-RIGHT, "V" in middle, animated tooltip to the left */}
      <div data-ai-chat-root className={`fixed bottom-5 right-5 z-[58] flex items-center gap-3 ${open ? "opacity-0 pointer-events-none" : "opacity-100"} transition-opacity duration-300`}>
        {/* Animated tooltip bubble (slides in from right toward the button on the right) */}
        <div
          className={`hidden sm:flex items-center pr-1 transition-all duration-500 ease-out ${showTooltip ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0 pointer-events-none"}`}
          aria-hidden={!showTooltip}
        >
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ai-tooltip-bubble relative bg-white text-black text-[12px] leading-tight px-4 py-2.5 rounded-2xl shadow-xl border border-black/10 max-w-[240px] cursor-pointer hover:border-black transition-colors"
          >
            <span className="ai-tooltip-text inline-block whitespace-nowrap">
              {T.tooltipText}
            </span>
            {/* Arrow on right pointing to the button */}
            <span className="absolute top-1/2 -translate-y-1/2 -right-1.5 w-3 h-3 rotate-45 bg-white border-r border-t border-black/10" />
          </button>
        </div>

        {/* Round launcher button — admin can override the centered "V" with
            an uploaded circular logo (see Avatar component above). */}
        <div className="ai-launcher-wrap relative inline-flex">
        <button
          onClick={() => setOpen(true)}
          className="ai-launcher-btn relative w-14 h-14 rounded-full bg-black text-white shadow-2xl border border-white/10 flex items-center justify-center hover:bg-[var(--accent)] hover:scale-105 active:scale-95 transition-all duration-200 overflow-hidden"
          aria-label="AI köməkçi"
          data-testid="ai-chat-launcher"
          type="button"
        >
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full ai-pulse-ring" />
          {/* Logo image if uploaded, else serif initial */}
          {hasLogo ? (
            <img
              src={cfg.logo_url}
              alt={T.headerTitle}
              className="absolute inset-0 w-full h-full object-cover rounded-full"
              loading="lazy"
            />
          ) : (
            <span className="font-serif font-medium text-2xl leading-none relative" style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', serif", letterSpacing: "0.02em" }}>{T.launcherLabel.charAt(0).toUpperCase()}</span>
          )}
          {/* Tiny sparkle accent */}
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--accent)] flex items-center justify-center z-10">
            <Sparkles size={7} className="text-white" strokeWidth={2.5} />
          </span>
        </button>
        </div>
      </div>

      {/* Backdrop on mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[58] md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Chat panel — bottom RIGHT */}
      <div
        className={`fixed z-[59] bg-white text-black border border-black/10 shadow-2xl flex flex-col overflow-hidden
          left-0 right-0 bottom-0 rounded-t-2xl
          md:right-5 md:left-auto md:bottom-5 md:rounded-2xl md:w-[400px]
          ${open ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 pointer-events-none"}
          transition-all duration-300 ease-out`}
        style={{ maxHeight: "min(640px, calc(100vh - 40px))", height: "min(640px, calc(100vh - 40px))" }}
        data-testid="ai-chat-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-black to-zinc-800 text-white">
          <div className="flex items-center gap-2.5">
            {hasLogo ? (
              <img
                src={cfg.logo_url}
                alt={T.headerTitle}
                className="w-9 h-9 rounded-full object-cover border border-white/15"
                loading="lazy"
              />
            ) : (
              <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center border border-white/15">
                <span className="font-serif text-lg leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{T.launcherLabel.charAt(0).toUpperCase()}</span>
              </span>
            )}
            <div>
              <div className="text-sm font-medium leading-none">{T.headerTitle}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-white/50 mt-1">{T.headerSubtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="text-[10px] tracking-[0.18em] uppercase text-white/50 hover:text-white px-2 py-1"
                type="button"
                data-testid="ai-chat-clear"
              >
                {T.clearButtonText}
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white"
              aria-label="Bağla"
              data-testid="ai-chat-close"
              type="button"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-50">
          {showWelcome && (
            <div className="flex gap-2.5 items-start">
              <Avatar size={32} />
              <div className="bg-white border border-black/5 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm leading-relaxed max-w-[88%] shadow-sm">
                {cfg.welcome}
                <div className="text-[10px] tracking-[0.16em] uppercase text-black/35 mt-2">{T.quickPromptsLabel}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {suggestedPrompts.map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 0); }}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-black/10 text-black/70 hover:border-black hover:text-black transition-colors"
                      type="button"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div key={idx} className={`flex gap-2.5 items-start ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              {m.role === "model" && <Avatar size={32} />}
              <div
                className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed max-w-[88%] shadow-sm ${
                  m.role === "user"
                    ? "bg-black text-white rounded-tr-sm whitespace-pre-wrap break-words"
                    : m.error
                    ? "bg-red-50 border border-red-100 text-red-900 rounded-tl-sm whitespace-pre-wrap break-words"
                    : "bg-white border border-black/5 text-black rounded-tl-sm"
                }`}
              >
                {renderMessageContent(m)}
              </div>
            </div>
          ))}

          {busy && (
            <div className="flex gap-2.5 items-start">
              <Avatar size={32} />
              <div className="bg-white border border-black/5 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm shadow-sm flex items-center gap-2 text-black/55">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
                {T.typingText}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={send} className="border-t border-black/10 bg-white px-3 py-3 flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder={T.inputPlaceholder}
            rows={1}
            className="flex-1 resize-none bg-zinc-100 border-0 rounded-xl px-3.5 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-black/10 max-h-32"
            data-testid="ai-chat-input"
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="w-10 h-10 shrink-0 rounded-full bg-black text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--accent)] transition-colors"
            aria-label="Göndər"
            data-testid="ai-chat-send"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
          </button>
        </form>

        {error && (
          <div className="absolute bottom-16 left-3 right-3 text-[11px] text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg" data-testid="ai-chat-error">
            {error}
          </div>
        )}
      </div>
    </>
  );
}
